import { Card } from '../../../molecules/Card/Card';
import { KPIText, type KPITextTone } from '../../../molecules/KPIText/KPIText';
import { NONE, cardState, pct, pts, spanClass, type WidgetBlockProps } from '../shared/cardShell';

export type ShareOfVoiceKpiView = {
  /** Our share of the category's mentions, in %; `null` when we have none (draws `—`). */
  share: number | null;
  /** The share's change in points against the comparison period; `null` when there is none. */
  shareGap: number | null;
  /** Whether any category mention is in scope. */
  hasRows: boolean;
};

export type ShareOfVoiceKpiCardProps = WidgetBlockProps & {
  view?: ShareOfVoiceKpiView;
  /** Which denominator the tooltip describes — the Brands chip's selection or every operator. */
  category: 'chip' | 'all';
};

/**
 * Figma `972:20769` — share of voice as a figure rather than a donut.
 *
 * Takes the **category's** rows, not ours: a share needs a denominator. It is
 * the same figure `ShareOfVoiceCard` shows, off the same counts, so the figure
 * at the top of a page and the figure in the middle of that card are one number
 * rather than two computations that agree by luck.
 *
 * In points, not percent, for the reason `ShareOfVoiceCard` records: a share
 * moving 34.8 → 36.1 is "+1.3 pts", and "+3.7%" reads as though we gained 3.7
 * points of the category.
 *
 * `category` picks the tooltip exactly as it does on the donut — whether the
 * denominator is the Brands chip's selection or every operator tracked. Same
 * prop, same two readings, so the two cards cannot describe the same number two
 * different ways on one screen.
 */
export function ShareOfVoiceKpiCard({
  status,
  view,
  span = 3,
  chrome,
  onRetry,
  category,
}: ShareOfVoiceKpiCardProps) {
  const hasData = view !== undefined && view.hasRows;
  const shareGap = view?.shareGap ?? null;
  const tone: KPITextTone | undefined =
    shareGap === null || shareGap === 0 ? undefined : shareGap > 0 ? 'positive' : 'negative';

  return (
    <Card
      {...chrome}
      className={spanClass(span)}
      size="sm"
      {...cardState(status, hasData, onRetry)}
      title="Share of voice"
      helpText={
        category === 'chip'
          ? 'Your brand’s mentions as a percentage of mentions across the selected brands.'
          : 'Your brand’s mentions as a percentage of mentions across all tracked brands.'
      }
      kpi={
        view && (
          <KPIText tone={tone} badge={shareGap === null ? undefined : pts(shareGap)}>
            {view.share === null ? NONE : pct(view.share)}
          </KPIText>
        )
      }
    />
  );
}
